import React from 'react';
import { useSelector } from 'react-redux';

// Types
import { GoalItem } from 'Shared/constants/types';

// Selectors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

// Components
import { WpDataTableV2, SortDirections } from 'Wpds/components/table-v2';
import GoalName from '../GoalName/GoalName';
import GoalOwner from '../GoalOwner/GoalOwner';
import GoalDueDate from '../GoalDueDate/GoalDueDate';
import GoalProgress from '../GoalProgress/GoalProgress';
import { WpBadge } from 'Wpds/components/badge';

export const ColumnsConst = {
  name: {
    label: 'Goal Name',
    canHide: false,
  },
  due: {
    label: 'Due',
    canHide: true,
  },
  owner: {
    label: 'Owner',
    canHide: true,
  },
  workspace: {
    label: 'Workspace',
    canHide: true,
  },
  department: {
    label: 'Department',
    canHide: true,
  },
  status: {
    label: 'Status',
    canHide: true,
  },
  progress: {
    label: 'Progress',
    canHide: true,
  },
  actions: {
    label: 'Actions',
    canHide: false,
  },
};

type Props = {
  goals: Array<GoalItem>;
  hiddenColumns?: Array<string>;
  loading?: boolean;
  onItemClick?: Function;
  allowOrphaned?: boolean;
  windowedScrolling?: boolean;
  maxHeight?: string;
};

const GoalsCascadingTableView = ({
  goals,
  hiddenColumns = [],
  onItemClick,
  allowOrphaned = false,
  windowedScrolling = false,
  maxHeight,
}: Props) => {
  const users = useSelector(state => UsersSelectors.getAllUsers(state));

  const getSubGoals = (goal: GoalItem) => {
    return goals.filter(g => g.parentId === goal.id).map(goal => ({ ...goal, children: getSubGoals(goal) }));
  };

  const formattedGoals = goals
    .filter(goal => {
      if (allowOrphaned) {
        if (goal.parentId && !goals.find(g => g.id === goal.parentId)) return true;
        else return !goal.parentId;
      } else return !goal.parentId;
    })
    .map(goal => ({ ...goal, children: getSubGoals(goal) }));

  const columns = [
    {
      label: ColumnsConst.name.label,
      selector: row => row.name,
      component: ({ row }) => <GoalName goal={row} showIcon={false} showStatusIndicator={true} />,
      maxWidth: '25rem',
    },
    {
      label: ColumnsConst.owner.label,
      selector: row => users.getIn([row.assigneeId, 'label']),
      component: ({ row }) => <GoalOwner goal={row} />,
      showColumn: !hiddenColumns.includes(ColumnsConst.owner.label),
    },
    {
      label: ColumnsConst.due.label,
      selector: row => row.endDate,
      component: ({ row }) => <GoalDueDate goal={row} />,
      sort: SortDirections.DSC,
      showColumn: !hiddenColumns.includes(ColumnsConst.due.label),
    },
    {
      label: ColumnsConst.progress.label,
      component: ({ row }: { row: GoalItem }) =>
        row.complete ? (
          <WpBadge color="green" variant="bold">
            complete
          </WpBadge>
        ) : (
          <GoalProgress goal={row} />
        ),
      showColumn: !hiddenColumns.includes(ColumnsConst.progress.label),
    },
  ];

  const baseClassName = 'GoalsCascadingTableView';
  return (
    <div className={baseClassName}>
      <WpDataTableV2
        tableId="goalsTable"
        data={formattedGoals}
        columns={columns}
        rowClick={onItemClick}
        showHeader
        showInContainer={true}
        nestedKey={row => row.children}
        showBorder={false}
        uniqueIdentifier={row => row.slug}
        labelIdentifier={row => row.name}
        pluralItemLabel="Sub-Goals"
        singleItemLabel="Sub-Goal"
        windowedScrolling={windowedScrolling}
        maxHeight={maxHeight}
        selectableSelector={row => row.uuid}
      />
    </div>
  );
};

export default GoalsCascadingTableView;
